import { Injectable } from '@angular/core';
import { MaintenanceAndTasksLogsMapped, Task } from '@core/interfaces';
import camelCaseToSnakeCase from '@core/utils/functions/camel-case-to-snake-case';
import { EnumPipe } from '@fuse/pipes/status/status.pipe';
import { TranslateService } from '@ngx-translate/core';
import { clone, isEqual } from 'lodash';
import moment from 'moment-timezone';

const iconList = {
  pending: 'icon-calendar-clock',
  scheduled: 'icon-clock',
  inProgress: 'icon-map-marker',
  onRoute: 'icon-truck',
  reported: 'icon-alert',
  canceled: 'icon-close',
  done: 'icon-check'
};

const imageList = {
  archive: 'ic_archive_18px',
  unarchive: 'ic_unarchive_18px'
};

@Injectable({
  providedIn: 'root'
})
export class CreateTaskLogsService {

  constructor(
    private translateService: TranslateService,
    private taskStatusPipe: EnumPipe,
  ) {}

  public listTaskLogsMapped(tasks: Task[]): MaintenanceAndTasksLogsMapped[] {
    const logsMapped: MaintenanceAndTasksLogsMapped[] = [];

    tasks.forEach((task, taskIndex) => {
      task.logs.forEach((currentTaskLog, logIndex) => {
        const previousTaskLog = task.logs[logIndex - 1];

        const changedStatusLog = this.generateStatusLog({
          taskIndex,
          currentTaskLog,
          previousTaskLog
        });

        if (changedStatusLog) {
          logsMapped.push(changedStatusLog);
        }

        const ratedLog = this.generateRatingLog({ currentTaskLog, previousTaskLog, taskIndex });
        if (ratedLog) {
          logsMapped.push(ratedLog);
        }

        const archivedLog = this.generateArchiveOrUnarchiveLog({
          currentTaskLog,
          previousTaskLog,
          taskIndex,
        });
        if (archivedLog) {
          logsMapped.push(archivedLog);
        }
      });
    });

    return logsMapped;
  }


  private generateStatusLog({ currentTaskLog, previousTaskLog, taskIndex }): MaintenanceAndTasksLogsMapped {
    const previous = (previousTaskLog && previousTaskLog.snapshot && previousTaskLog.snapshot.status) || null;
    const current = (currentTaskLog.snapshot && currentTaskLog.snapshot.status) || null;

    if (previous === current) {
      return null;
    }

    const currentInSnakeCase = camelCaseToSnakeCase(String(currentTaskLog.snapshot && currentTaskLog.snapshot.status));
    const currentDescription = currentTaskLog.snapshot && currentTaskLog.snapshot.statusDescription;

    const eventNoteList = {
      reported: this.translateService.instant('MAINTENANCES.TABS.LOGS.TASK.NOTE.REASON', {
        reason: currentDescription || this.translateService.instant('MAINTENANCES.TABS.LOGS.TASK.NOTE.NOT_COMPLETED')
      }),
      canceled: this.translateService.instant('MAINTENANCES.TABS.LOGS.TASK.NOTE.REASON', {
        reason: currentDescription || this.translateService.instant('MAINTENANCES.TABS.LOGS.TASK.NOTE.NOT_COMPLETED')
      }),
      done: this.translateService.instant('MAINTENANCES.TABS.LOGS.TASK.NOTE.CONCLUSION', {
        conclusion: currentDescription || this.translateService.instant('MAINTENANCES.TABS.LOGS.TASK.NOTE.NOT_COMPLETED')
      }),
    };

    if (!previous && current === 'scheduled') {
      currentTaskLog.eventDescription = this.translateService.instant('MAINTENANCES.TABS.LOGS.TASK.DESCRIPTION.NEW');
    } else if (!previous && current !== 'scheduled') {
      currentTaskLog.eventDescription = this.translateService.instant('MAINTENANCES.TABS.LOGS.TASK.DESCRIPTION.UPDATED_WITHOUT_PREVIOUS', {
        currentStatus: this.taskStatusPipe.transform(String(current), 'task_status').toLowerCase()
      });
    } else {
      currentTaskLog.eventDescription = this.translateService.instant('MAINTENANCES.TABS.LOGS.TASK.DESCRIPTION.UPDATED', {
        previousStatus: this.taskStatusPipe.transform(String(previous), 'task_status').toLowerCase(),
        currentStatus: this.taskStatusPipe.transform(String(current), 'task_status').toLowerCase()
      });
    }

    return {
      ...clone(currentTaskLog),
      icon: iconList[current],
      isCreatedLog: !previousTaskLog,
      typeLog: `task_${currentTaskLog.snapshot.id}`,
      colorBackground: `${currentInSnakeCase}--background`,
      dateCreatedAt: moment(currentTaskLog.createdAt).local().format('DD/MM/YYYY'),
      dateTimeCreatedAt: moment(currentTaskLog.createdAt).local().format('DD/MM/YYYY HH:mm:ss'),
      eventNote: ['reported', 'canceled', 'done'].includes(current) && eventNoteList[current],
      eventName: this.translateService.instant('MAINTENANCES.TABS.LOGS.TASK.STATUS', {
        taskIndex: taskIndex + 1,
        class: currentInSnakeCase,
        status: this.taskStatusPipe.transform(String(current), 'task_status').toLowerCase()
      })
    };
  }

  private generateRatingLog({ currentTaskLog, previousTaskLog, taskIndex }): MaintenanceAndTasksLogsMapped {
    const previousRating = (previousTaskLog && previousTaskLog.snapshot && previousTaskLog.snapshot.rating) || null;
    const currentRating = (currentTaskLog && currentTaskLog.snapshot && currentTaskLog.snapshot.rating) || null;
    if (isEqual(currentRating, previousRating)) {
      return null;
    }

    let starColor = 'positive-stars';
    if (currentTaskLog.snapshot.rating.stars === 3) {
      starColor = 'reasonable-stars';
    } else if (currentTaskLog.snapshot.rating.stars < 3) {
      starColor = 'negative-stars';
    }

    return {
      ...clone(currentTaskLog),
      icon: 'icon-star',
      isCreatedLog: false,
      isRateLog: true,
      typeLog: `task_${currentTaskLog.snapshot.id}`,
      colorBackground: `${starColor}--background`,
      dateCreatedAt: moment(currentTaskLog.createdAt).local().format('DD/MM/YYYY'),
      dateTimeCreatedAt: moment(currentTaskLog.createdAt).local().format('DD/MM/YYYY HH:mm:ss'),
      eventDescription: currentTaskLog.snapshot.rating.comments || this.translateService.instant('MAINTENANCES.TABS.LOGS.TASK.DESCRIPTION.NO_COMMENTS'),
      eventName: this.translateService.instant(`MAINTENANCES.TABS.LOGS.TASK.RATED`, {
        taskIndex: taskIndex + 1
      })
    };
  }

  private generateArchiveOrUnarchiveLog({ currentTaskLog, previousTaskLog, taskIndex }): MaintenanceAndTasksLogsMapped {
    const previousArchived = previousTaskLog && previousTaskLog.snapshot && previousTaskLog.snapshot.archived;
    const currentArchived = currentTaskLog.snapshot && currentTaskLog.snapshot.archived;
    if (!previousTaskLog || isEqual(currentArchived, previousArchived)) {
      return null;
    }

    const archived = currentArchived ? 'archive' : 'unarchive';

    return {
      ...clone(currentTaskLog),
      image: imageList[archived],
      isCreatedLog: false,
      typeLog: `task_${currentTaskLog.snapshot.id}`,
      colorBackground: `${archived}--background`,
      dateCreatedAt: moment(currentTaskLog.createdAt).local().format('DD/MM/YYYY'),
      dateTimeCreatedAt: moment(currentTaskLog.createdAt).local().format('DD/MM/YYYY HH:mm:ss'),
      eventName: this.translateService.instant(`MAINTENANCES.TABS.LOGS.TASK.${archived.toUpperCase()}`, {
        taskIndex: taskIndex + 1
      })
    };
  }
}
